/* ==========================================================================
   QEVANO — theme.js
   Vanilla JavaScript. No dependencies.
   ========================================================================== */

(function () {
  'use strict';

  var config = window.QEVANO || {};
  var routes = config.routes || {};

  /* ---------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------- */

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(el, type, handler, options) {
    if (el) el.addEventListener(type, handler, options || false);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var scrollLockCount = 0;

  function lockScroll() {
    scrollLockCount += 1;
    document.documentElement.classList.add('scroll-lock');
    document.body.classList.add('scroll-lock');
  }

  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.documentElement.classList.remove('scroll-lock');
      document.body.classList.remove('scroll-lock');
    }
  }

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function handler(event) {
      if (event.key === 'Escape') {
        if (typeof onEscape === 'function') onEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusables = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    container.__trapHandler = handler;
    document.addEventListener('keydown', handler);
  }

  function releaseFocus(container) {
    if (container && container.__trapHandler) {
      document.removeEventListener('keydown', container.__trapHandler);
      delete container.__trapHandler;
    }
  }

  function announce(message) {
    var region = $('[data-cart-live-region]');
    if (region) region.textContent = message;
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var format = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    return format ? amount + ' ' + format : amount;
  }

  /* ---------------------------------------------------------------------
     Scroll reveal
     --------------------------------------------------------------------- */

  function initReveal() {
    var items = $$('.reveal');
    if (!items.length) return;

    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      items.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
     Sticky header
     --------------------------------------------------------------------- */

  function initHeader() {
    var wrapper = $('[data-header-wrapper]');
    if (!wrapper || wrapper.__bound) return;
    wrapper.__bound = true;

    var lastState = false;

    function update() {
      var stuck = window.scrollY > 12;
      if (stuck !== lastState) {
        wrapper.classList.toggle('is-stuck', stuck);
        lastState = stuck;
      }
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Mobile navigation
     --------------------------------------------------------------------- */

  var closeMobileNav = function () {};

  function initMobileNav() {
    var nav = $('[data-mobile-nav]');
    if (!nav || nav.__bound) return;
    nav.__bound = true;
    var opener = $('[data-mobile-nav-open]');

    function open() {
      nav.classList.add('is-open');
      nav.setAttribute('aria-hidden', 'false');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      lockScroll();
      trapFocus(nav, close);
      var first = $(FOCUSABLE, nav);
      if (first) first.focus();
    }

    function close(returnFocus) {
      if (!nav.classList.contains('is-open')) return;
      nav.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      if (opener) opener.setAttribute('aria-expanded', 'false');
      unlockScroll();
      releaseFocus(nav);
      if (opener && returnFocus !== false) opener.focus();
    }

    closeMobileNav = function () {
      close(false);
    };

    on(opener, 'click', open);
    $$('[data-mobile-nav-close]', nav).forEach(function (el) {
      on(el, 'click', function () {
        close();
      });
    });
  }

  /* ---------------------------------------------------------------------
     Search overlay
     --------------------------------------------------------------------- */

  function initSearch() {
    var overlay = $('[data-search-overlay]');
    if (!overlay) return;

    function open() {
      closeMobileNav();
      overlay.hidden = false;
      lockScroll();
      trapFocus(overlay, close);
      var input = $('input[type="search"]', overlay);
      if (input) window.setTimeout(function () { input.focus(); }, 60);
    }

    function close() {
      overlay.hidden = true;
      unlockScroll();
      releaseFocus(overlay);
    }

    $$('[data-search-open]').forEach(function (el) {
      if (el.__bound) return;
      el.__bound = true;
      on(el, 'click', function (event) {
        event.preventDefault();
        open();
      });
    });

    $$('[data-search-close]', overlay).forEach(function (el) {
      if (el.__bound) return;
      el.__bound = true;
      on(el, 'click', close);
    });
  }

  /* ---------------------------------------------------------------------
     Accordions
     --------------------------------------------------------------------- */

  function initAccordions(scope) {
    $$('[data-accordion-trigger]', scope).forEach(function (trigger) {
      if (trigger.__bound) return;
      trigger.__bound = true;

      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      if (trigger.getAttribute('aria-expanded') === 'true') {
        panel.style.height = 'auto';
        panel.classList.add('is-open');
      }

      on(trigger, 'click', function () {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        var group = trigger.closest('[data-accordion]');

        if (group && group.getAttribute('data-accordion-single') === 'true' && !expanded) {
          $$('[data-accordion-trigger]', group).forEach(function (other) {
            if (other === trigger) return;
            var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
            other.setAttribute('aria-expanded', 'false');
            if (otherPanel) collapse(otherPanel);
          });
        }

        trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (expanded) {
          collapse(panel);
        } else {
          expand(panel);
        }
      });
    });

    function expand(panel) {
      var inner = panel.firstElementChild;
      if (!inner) return;
      panel.classList.add('is-open');
      panel.style.height = inner.offsetHeight + 'px';
      window.setTimeout(function () {
        if (panel.style.height !== '0px') panel.style.height = 'auto';
      }, 420);
    }

    function collapse(panel) {
      var inner = panel.firstElementChild;
      if (!inner) return;
      panel.classList.remove('is-open');
      panel.style.height = inner.offsetHeight + 'px';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          panel.style.height = '0px';
        });
      });
    }
  }

  /* ---------------------------------------------------------------------
     Quantity inputs
     --------------------------------------------------------------------- */

  function initQuantity(scope) {
    $$('[data-quantity]', scope).forEach(function (widget) {
      if (widget.__bound) return;
      widget.__bound = true;
      var input = $('input', widget);
      if (!input) return;

      $$('[data-quantity-change]', widget).forEach(function (button) {
        on(button, 'click', function () {
          var delta = parseInt(button.getAttribute('data-quantity-change'), 10);
          var min = parseInt(input.getAttribute('min') || '1', 10);
          var next = (parseInt(input.value, 10) || min) + delta;
          if (next < min) next = min;
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Cart drawer
     --------------------------------------------------------------------- */

  var CartDrawer = {
    el: null,

    init: function () {
      this.el = $('[data-cart-drawer]');
      var self = this;

      $$('[data-cart-drawer-open]').forEach(function (el) {
        if (el.__bound) return;
        el.__bound = true;
        on(el, 'click', function (event) {
          if (!self.el) return;
          event.preventDefault();
          self.open();
        });
      });

      this.bindPanel();
    },

    bindPanel: function () {
      if (!this.el) return;
      var self = this;

      $$('[data-cart-drawer-close]', this.el).forEach(function (el) {
        if (el.__bound) return;
        el.__bound = true;
        on(el, 'click', function () {
          self.close();
        });
      });
    },

    open: function () {
      if (!this.el) return;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      lockScroll();
      trapFocus(this.el, this.close.bind(this));
      var first = $(FOCUSABLE, this.el);
      if (first) first.focus();
    },

    close: function () {
      if (!this.el) return;
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      unlockScroll();
      releaseFocus(this.el);
    },

    render: function (sections) {
      if (!sections) return;

      if (sections['cart-drawer'] && this.el) {
        var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        var fresh = parsed.querySelector('[data-cart-drawer-content]');
        var current = $('[data-cart-drawer-content]', this.el);
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          this.bindPanel();
          initQuantity(this.el);
          bindCartActions(this.el);
          initProductForms();
        }
      }

      if (sections.header) {
        var headerDoc = new DOMParser().parseFromString(sections.header, 'text/html');
        var freshCount = headerDoc.querySelector('[data-cart-count]');
        $$('[data-cart-count]').forEach(function (node) {
          if (!freshCount) return;
          node.textContent = freshCount.textContent;
          node.hidden = freshCount.hasAttribute('hidden');
        });
      }

      if (sections['main-cart']) {
        var cartDoc = new DOMParser().parseFromString(sections['main-cart'], 'text/html');
        var freshCart = cartDoc.querySelector('[data-cart-page-content]');
        var currentCart = $('[data-cart-page-content]');
        if (freshCart && currentCart) {
          currentCart.innerHTML = freshCart.innerHTML;
          initQuantity(currentCart);
          bindCartActions(currentCart);
          initProductForms();
        }
      }
    }
  };

  function sectionsToRequest() {
    var list = [];
    if ($('[data-cart-drawer]')) list.push('cart-drawer');
    if ($('[data-section-type="header"]')) list.push('header');
    if ($('[data-cart-page-content]')) list.push('main-cart');
    return list.join(',');
  }

  function cartRequest(url, body, button) {
    if (button) button.classList.add('is-loading');
    body.sections = sectionsToRequest();
    body.sections_url = window.location.pathname;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (button) button.classList.remove('is-loading');
        if (!result.ok) {
          var message = (result.data && (result.data.description || result.data.message)) || config.strings.cartError;
          announce(message);
          throw new Error(message);
        }
        CartDrawer.render(result.data.sections);
        return result.data;
      })
      .catch(function (error) {
        if (button) button.classList.remove('is-loading');
        throw error;
      });
  }

  function bindCartActions(scope) {
    $$('[data-cart-remove]', scope).forEach(function (el) {
      if (el.__bound) return;
      el.__bound = true;
      on(el, 'click', function (event) {
        event.preventDefault();
        var line = el.getAttribute('data-cart-remove');
        cartRequest(routes.cart_change_url, { line: parseInt(line, 10), quantity: 0 }, null).then(function () {
          announce(config.strings.cartUpdated || 'Cart updated');
        });
      });
    });

    $$('[data-cart-quantity]', scope).forEach(function (input) {
      if (input.__boundQty) return;
      input.__boundQty = true;
      on(input, 'change', function () {
        var line = input.getAttribute('data-cart-quantity');
        var qty = parseInt(input.value, 10);
        if (isNaN(qty) || qty < 0) qty = 0;
        cartRequest(routes.cart_change_url, { line: parseInt(line, 10), quantity: qty }, null);
      });
    });

    $$('[data-cart-note]', scope).forEach(function (field) {
      if (field.__bound) return;
      field.__bound = true;
      on(field, 'change', function () {
        fetch(routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: field.value })
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Add-to-cart forms
     --------------------------------------------------------------------- */

  function initProductForms() {
    $$('[data-product-form]').forEach(function (form) {
      if (form.__bound) return;
      form.__bound = true;

      on(form, 'submit', function (event) {
        if (!config.cartDrawerEnabled) return; // let Shopify handle full page post
        event.preventDefault();

        var button = $('[data-add-to-cart]', form);
        var idInput = form.querySelector('[name="id"]');
        var qtyInput = form.querySelector('[name="quantity"]');
        if (!idInput || !idInput.value) {
          var missing = $('[data-form-status]', form);
          if (missing) {
            missing.textContent = config.strings.unavailable;
            missing.hidden = false;
          }
          return;
        }

        var items = [];
        var extra = form.querySelectorAll('[data-extra-item]');

        items.push({
          id: parseInt(idInput.value, 10),
          quantity: parseInt((qtyInput && qtyInput.value) || '1', 10)
        });

        Array.prototype.forEach.call(extra, function (node) {
          var id = parseInt(node.getAttribute('data-extra-item'), 10);
          var qty = parseInt(node.getAttribute('data-extra-quantity') || '1', 10);
          if (id) items.push({ id: id, quantity: qty });
        });

        cartRequest(routes.cart_add_url, { items: items }, button)
          .then(function () {
            announce('Added to cart');
            CartDrawer.open();
          })
          .catch(function (error) {
            var status = $('[data-form-status]', form);
            if (status) {
              status.textContent = error.message;
              status.hidden = false;
            }
          });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Variant selection
     --------------------------------------------------------------------- */

  function initVariantSelectors() {
    $$('[data-variant-selector]').forEach(function (root) {
      if (root.__bound) return;
      root.__bound = true;

      var dataEl = $('[data-variant-json]', root);
      if (!dataEl) return;

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      var form = root.closest('form') || $('[data-product-form]', root);
      var idInput = root.querySelector('[data-variant-id]') || (form && form.querySelector('[name="id"]'));
      var priceEl = $('[data-variant-price]', root);
      var compareEl = $('[data-variant-compare]', root);
      var button = $('[data-add-to-cart]', root) || (form && $('[data-add-to-cart]', form));

      function currentOptions() {
        var values = [];
        $$('[data-option-index]', root).forEach(function (group) {
          var index = parseInt(group.getAttribute('data-option-index'), 10);
          var checked = group.querySelector('input:checked') || group.querySelector('select');
          if (checked) values[index] = checked.value;
        });
        return values;
      }

      function matchVariant(values) {
        if (!values.length) {
          for (var k = 0; k < variants.length; k++) {
            if (variants[k].available) return variants[k];
          }
          return variants[0] || null;
        }
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var match = true;
          for (var j = 0; j < values.length; j++) {
            if (values[j] !== undefined && v.options[j] !== values[j]) {
              match = false;
              break;
            }
          }
          if (match) return v;
        }
        return null;
      }

      function update() {
        var variant = matchVariant(currentOptions());
        if (!variant) {
          if (button) {
            button.setAttribute('disabled', 'disabled');
            var labelA = $('[data-add-label]', button);
            if (labelA) labelA.textContent = config.strings.unavailable;
          }
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (priceEl) priceEl.innerHTML = variant.price_formatted || formatMoney(variant.price);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.innerHTML = variant.compare_at_price_formatted || formatMoney(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        if (button) {
          var label = $('[data-add-label]', button);
          if (variant.available) {
            button.removeAttribute('disabled');
            if (label) label.textContent = config.strings.addToCart;
          } else {
            button.setAttribute('disabled', 'disabled');
            if (label) label.textContent = config.strings.soldOut;
          }
        }

        if (variant.featured_media_id) {
          var thumb = $('[data-gallery-thumb][data-media-id="' + variant.featured_media_id + '"]');
          if (thumb) thumb.click();
        }

        if (history.replaceState && root.getAttribute('data-update-url') === 'true') {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url.toString());
        }
      }

      $$('[data-option-index] input, [data-option-index] select', root).forEach(function (input) {
        on(input, 'change', update);
      });

      update();
    });
  }

  /* ---------------------------------------------------------------------
     Bundle selector
     --------------------------------------------------------------------- */

  function initBundles() {
    $$('[data-bundle-group]').forEach(function (group) {
      if (group.__bound) return;
      group.__bound = true;

      var form = document.getElementById(group.getAttribute('data-bundle-form')) || group.closest('form');
      if (!form) return;

      function apply(input) {
        var idField = form.querySelector('[name="id"]');
        var qtyField = form.querySelector('[name="quantity"]');
        var variantId = input.getAttribute('data-bundle-variant');
        var qty = input.getAttribute('data-bundle-quantity') || '1';

        if (idField && variantId) idField.value = variantId;
        if (qtyField) qtyField.value = qty;

        var priceTarget = $('[data-bundle-total]', form) || $('[data-bundle-total]');
        var priceText = input.getAttribute('data-bundle-price');
        if (priceTarget && priceText) priceTarget.innerHTML = priceText;

        var compareTarget = $('[data-bundle-compare]', form) || $('[data-bundle-compare]');
        var compareText = input.getAttribute('data-bundle-compare-price');
        if (compareTarget) {
          if (compareText) {
            compareTarget.innerHTML = compareText;
            compareTarget.hidden = false;
          } else {
            compareTarget.hidden = true;
          }
        }
      }

      var radios = $$('input[type="radio"]', group);
      var hasChecked = radios.some(function (input) {
        return input.checked;
      });

      if (!hasChecked) {
        for (var i = 0; i < radios.length; i++) {
          if (!radios[i].disabled) {
            radios[i].checked = true;
            break;
          }
        }
      }

      radios.forEach(function (input) {
        on(input, 'change', function () {
          if (input.checked) apply(input);
        });
        if (input.checked) apply(input);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Product gallery
     --------------------------------------------------------------------- */

  function initGallery() {
    $$('[data-gallery]').forEach(function (gallery) {
      if (gallery.__bound) return;
      gallery.__bound = true;

      var main = $('[data-gallery-main]', gallery);
      var thumbs = $$('[data-gallery-thumb]', gallery);
      if (!main || !thumbs.length) return;

      thumbs.forEach(function (thumb) {
        on(thumb, 'click', function () {
          var src = thumb.getAttribute('data-image');
          var srcset = thumb.getAttribute('data-srcset');
          var alt = thumb.getAttribute('data-alt') || '';
          var img = $('img', main);
          if (img && src) {
            img.setAttribute('src', src);
            if (srcset) img.setAttribute('srcset', srcset);
            img.setAttribute('alt', alt);
          }
          thumbs.forEach(function (t) {
            t.classList.remove('is-active');
            t.setAttribute('aria-current', 'false');
          });
          thumb.classList.add('is-active');
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Horizontal carousels (testimonials)
     --------------------------------------------------------------------- */

  function initCarousels() {
    $$('[data-carousel]').forEach(function (carousel) {
      if (carousel.__bound) return;
      carousel.__bound = true;

      var track = $('[data-carousel-track]', carousel);
      var prev = $('[data-carousel-prev]', carousel);
      var next = $('[data-carousel-next]', carousel);
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        return first ? first.offsetWidth + 16 : 320;
      }

      function updateButtons() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      }

      on(prev, 'click', function () {
        track.scrollBy({ left: -step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      });

      on(next, 'click', function () {
        track.scrollBy({ left: step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      });

      on(track, 'scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons);
      updateButtons();
    });
  }

  /* ---------------------------------------------------------------------
     Collection sorting / facets
     --------------------------------------------------------------------- */

  function initFacets() {
    $$('[data-auto-submit]').forEach(function (el) {
      if (el.__bound) return;
      el.__bound = true;
      on(el, 'change', function () {
        var form = el.closest('form');
        if (form) form.submit();
      });
    });
  }

  /* ---------------------------------------------------------------------
     Init
     --------------------------------------------------------------------- */

  function initAll() {
    initReveal();
    initHeader();
    initMobileNav();
    initSearch();
    initAccordions(document);
    initQuantity(document);
    CartDrawer.init();
    bindCartActions(document);
    initProductForms();
    initVariantSelectors();
    initBundles();
    initGallery();
    initCarousels();
    initFacets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Shopify Theme Editor support */
  document.addEventListener('shopify:section:load', function () {
    initAll();
  });
  document.addEventListener('shopify:section:select', function (event) {
    if (event.target.querySelector('[data-cart-drawer]')) CartDrawer.open();
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    if (event.target.querySelector('[data-cart-drawer]')) CartDrawer.close();
  });
})();
